import { useLingui } from '@lingui/react/macro'
import { GeckoNotificationProvider } from 'features/notifications'
import {
  ActiveTimeRangeSelector,
  ScatterplotProvider,
  ScatterplotSidebar,
  TimeSeriesScatterplotContent,
  useScatterplotContext,
} from 'features/timeSeries'
import { ScatterplotSidebarView } from 'features/timeSeries/components/ScatterplotSidebar'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useSentinelStudioFocusSignal } from 'features/timeSeries/sentinelStudio/SentinelStudioContext'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { EmbeddedLeftRail, EmbeddedRightRail } from './EmbeddedChartRails'
import { EmbeddedChartToolbar } from './EmbeddedChartToolbar'

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`

const ScatterplotAndSidebarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  align-items: stretch;
  overflow: visible;
  position: relative;
  background: #0a0a0a;
  /* Lets the rails and the Models panel overlay the plot in tight spaces. */
  container: chartArea / inline-size;
`

const ChartWithFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

const ChartFooter = styled.div`
  padding: 0.25rem 0.375rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${CHART_COLORS.BACKGROUND};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const MODELS_TABS = [ScatterplotSidebarView.AGENT]

interface TimeSeriesScatterplotProps {
  chartID: string
  siteID: string
  /**
   * Render the workbench-style toolbar (save, pen display, markup, Models,
   * export, rail toggles) above the chart. Without it the footer carries the
   * rail toggles instead.
   */
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  /** No Tags / Selected Tags rails and no Models panel: just the plot. */
  hideSidebar?: boolean
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
  /** Suppress the toolbar even when `showToolbar` is set. */
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
  /** Initial sensor IDs to load when no chart config exists (e.g., from URL params) */
  initialSensorIds?: string[]
  /** Seed custom line colors per sensor id (applied once per id). */
  initialSensorColors?: Record<string, string>
  /** Sensor IDs rendered de-emphasized (dotted + translucent). */
  deemphasizedSensorIds?: string[]
  /** Initial time range to display; falls back to the last 7 days when omitted */
  initialTimeRange?: { from: string; to: string }
  /** Sensor IDs that should override the loaded chart selection once on mount */
  overrideSensorIds?: string[]
  /** Apply `overrideSensorIds` additively, preserving manually added sensors. */
  preserveManualSensors?: boolean
  /** Time range that should override the loaded chart viewport once on mount */
  overrideTimeRange?: { from: string; to: string }
  /** Manual alert that should be selected when the chart loads */
  initialManualAlertId?: string
  /** Whether saved alerts can be deleted from the alert detail popover. */
  allowSavedAlertDelete?: boolean
  /** Keeps the chart in a loading state while upstream context is resolving. */
  isInitializing?: boolean
  /** Hide the Save, Manual Alert, and Export PNG floating actions. */
  hideFloatingActions?: boolean
  /**
   * Ad-hoc mode: skip resolving `chartID` (no 404 toast) while keeping save
   * functional. First save promotes the transient slug into a persisted chart.
   */
  transient?: boolean
  /** Seed value for the editable chart name. Only meaningful for transient mode. */
  initialChartName?: string
  /**
   * Persist live chart state to localStorage and hydrate it on mount with
   * precedence over the saved chart config. Opt-in; the standalone Process
   * Data pages enable it.
   */
  persistLocalState?: boolean
}

interface TimeSeriesScatterplotInnerProps {
  showToolbar?: boolean
  showChartSettings?: boolean
  chartAreaRef?: React.RefObject<HTMLDivElement>
  unitID?: string
  unitName?: string
  children?: React.ReactNode
  chartContainer?: React.ComponentType<{ children: React.ReactNode }>
  disableSaving?: boolean
  hideSidebar?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
  toggleChatbot?: () => void
  initialManualAlertId?: string
  allowSavedAlertDelete?: boolean
  isInitializing?: boolean
  hideFloatingActions?: boolean
}

const TimeSeriesScatterplotInner: React.FC<
  TimeSeriesScatterplotInnerProps & {
    sidebarView: ScatterplotSidebarView
    setSidebarView: React.Dispatch<React.SetStateAction<ScatterplotSidebarView>>
  }
> = ({
  showToolbar = false,
  showChartSettings = true,
  chartAreaRef,
  unitID,
  unitName,
  children,
  chartContainer: ChartContainerComponent,
  disableSaving = false,
  sidebarView,
  setSidebarView,
  hideSidebar = false,
  hideHeader = false,
  hideFooter = false,
  toggleChatbot,
  initialManualAlertId,
  allowSavedAlertDelete,
  isInitializing = false,
  hideFloatingActions = false,
}) => {
  const { t } = useLingui()

  const {
    sensorEntries,
    selectedSensors,
    timeRange,
    setTimeRange,
    liveTail,
    resetViewport,
  } = useScatterplotContext()
  const localChartAreaRef = useRef<HTMLDivElement>(null)
  const areaRef = chartAreaRef ?? localChartAreaRef

  const showRails = !hideSidebar
  const toolbarVisible = showToolbar && !hideHeader
  const [leftOpen, setLeftOpen] = useState(false)
  const [rightOpen, setRightOpen] = useState(false)
  const [modelsOpen, setModelsOpen] = useState(false)

  const toggleLeft = useCallback(() => setLeftOpen((open) => !open), [])
  const toggleRight = useCallback(() => {
    setRightOpen((open) => !open)
    setModelsOpen(false)
  }, [])
  const toggleModels = useCallback(() => {
    setModelsOpen((open) => !open)
    setRightOpen(false)
  }, [])
  const closeModels = useCallback(() => setModelsOpen(false), [])

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)

    return () => clearTimeout(timer)
  }, [sidebarView, leftOpen, rightOpen, modelsOpen])

  // Clicking a case bubble on the chart pops the Models panel open on the
  // Sentinels tab so the case is right there — "so you know what you're
  // looking at". The detail view itself lands on the case (see SentinelsTab).
  const focusSignal = useSentinelStudioFocusSignal()
  const lastFocusNonce = useRef(0)
  useEffect(() => {
    if (!focusSignal || focusSignal.nonce === lastFocusNonce.current) return
    lastFocusNonce.current = focusSignal.nonce
    if (!showRails) return
    setSidebarView(ScatterplotSidebarView.SENTINELS)
    setModelsOpen(true)
    setRightOpen(false)
  }, [focusSignal, showRails, setSidebarView])

  const footerRailToggles =
    !toolbarVisible && showRails
      ? {
          onSettingsClick: toggleLeft,
          onPenDrawerClick: toggleRight,
          isSettingsOpen: leftOpen,
          isPenDrawerOpen: rightOpen,
        }
      : {}

  return (
    <>
      {children}
      {ChartContainerComponent ? (
        <ChartContainerComponent>
          <MainContainer>
            {toolbarVisible && (
              <EmbeddedChartToolbar
                leftOpen={leftOpen}
                rightOpen={rightOpen}
                modelsOpen={modelsOpen}
                onToggleLeft={toggleLeft}
                onToggleRight={toggleRight}
                onToggleModels={toggleModels}
                showRails={showRails}
                canSave={!disableSaving}
                chartAreaRef={areaRef}
              />
            )}
            <ScatterplotAndSidebarsContainer>
              {showRails && leftOpen && <EmbeddedLeftRail />}
              <ChartWithFooterWrapper>
                <TimeSeriesScatterplotContent
                  ref={areaRef}
                  showChartSettings={showChartSettings}
                  unitID={unitID}
                  unitName={unitName}
                  toggleChatbot={toggleChatbot}
                  hideTooltip={rightOpen}
                  initialManualAlertId={initialManualAlertId}
                  allowSavedAlertDelete={allowSavedAlertDelete}
                  isInitializing={isInitializing}
                  hideFloatingActions={hideFloatingActions || toolbarVisible}
                  hideMarkupHud={toolbarVisible}
                />
                {!hideFooter && (
                  <ChartFooter>
                    <ActiveTimeRangeSelector
                      timeRange={timeRange}
                      setTimeRange={setTimeRange}
                      liveTail={liveTail}
                      onResetViewport={resetViewport}
                      {...footerRailToggles}
                    />
                  </ChartFooter>
                )}
              </ChartWithFooterWrapper>
              {showRails && modelsOpen && (
                <ScatterplotSidebar
                  onClose={closeModels}
                  tagsOptions={sensorEntries}
                  selectedTags={selectedSensors}
                  setSidebarView={setSidebarView}
                  sidebarView={sidebarView}
                  visibleTabs={MODELS_TABS}
                  title={t`Models`}
                />
              )}
              {showRails && rightOpen && <EmbeddedRightRail />}
            </ScatterplotAndSidebarsContainer>
          </MainContainer>
        </ChartContainerComponent>
      ) : (
        <MainContainer>
          <TimeSeriesScatterplotContent
            ref={areaRef}
            showChartSettings={showChartSettings}
            unitID={unitID}
            unitName={unitName}
            toggleChatbot={toggleChatbot}
            initialManualAlertId={initialManualAlertId}
            allowSavedAlertDelete={allowSavedAlertDelete}
            isInitializing={isInitializing}
            hideFloatingActions={hideFloatingActions}
          />
        </MainContainer>
      )}
    </>
  )
}

export const TimeSeriesScatterplot: React.FC<TimeSeriesScatterplotProps> = ({
  chartID,
  siteID,
  disableSaving,
  hideSidebar = false,
  onChartSaved,
  hideHeader = false,
  hideFooter = false,
  initialSensorIds,
  initialSensorColors,
  deemphasizedSensorIds,
  initialTimeRange,
  overrideSensorIds,
  preserveManualSensors,
  overrideTimeRange,
  transient,
  initialChartName,
  persistLocalState,
  isInitializing = false,
  ...props
}) => {
  const [sidebarView, setSidebarView] = useState<ScatterplotSidebarView>(
    ScatterplotSidebarView.SENTINELS
  )

  return (
    <GeckoNotificationProvider>
      <ScatterplotProvider
        chartID={chartID}
        siteID={siteID}
        disableSaving={disableSaving}
        onChartSaved={onChartSaved}
        initialSensorIds={initialSensorIds}
        initialSensorColors={initialSensorColors}
        deemphasizedSensorIds={deemphasizedSensorIds}
        initialTimeRange={initialTimeRange}
        overrideSensorIds={overrideSensorIds}
        preserveManualSensors={preserveManualSensors}
        overrideTimeRange={overrideTimeRange}
        transient={transient}
        initialChartName={initialChartName}
        persistLocalState={persistLocalState}>
        <TimeSeriesScatterplotInner
          {...props}
          disableSaving={disableSaving}
          isInitializing={isInitializing}
          sidebarView={sidebarView}
          setSidebarView={setSidebarView}
          hideSidebar={hideSidebar}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
        />
      </ScatterplotProvider>
    </GeckoNotificationProvider>
  )
}
